<template>
  <aside
      class="settlement-section relative overflow-hidden rounded-3xl border border-white/10 bg-slate-950 text-slate-100 shadow-2xl"
      dir="rtl"
  >
    <div class="pointer-events-none absolute -top-24 -right-24 h-64 w-64 rounded-full bg-blue-500/10 blur-3xl"></div>
    <div class="pointer-events-none absolute -bottom-24 -left-20 h-72 w-72 rounded-full bg-emerald-500/10 blur-3xl"></div>

    <header class="relative z-10 border-b border-white/10 bg-white/[0.03] px-4 py-4 backdrop-blur-xl">
      <div class="flex items-center justify-between gap-3 min-w-0">
        <div class="flex items-center gap-3 min-w-0">
          <div class="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl border border-blue-400/25 bg-blue-400/10 text-blue-200">
            <i class="fas fa-wallet text-sm"></i>
          </div>

          <div class="min-w-0">
            <h3 class="truncate text-sm font-black text-white">
              {{ title }}
            </h3>
            <p class="mt-1 truncate text-[11px] text-slate-400">
              {{ subtitle }}
            </p>
          </div>
        </div>

        <button
            @click="fetchSettlements"
            :disabled="loading"
            class="shrink-0 rounded-2xl border border-white/10 bg-white/[0.04] px-3 py-2 text-xs font-bold text-slate-200 transition hover:border-blue-400/30 hover:text-blue-200 disabled:opacity-50"
            title="بروزرسانی"
        >
          {{ loading ? '...' : 'بروزرسانی' }}
        </button>
      </div>
    </header>

    <div class="relative z-10 p-4 space-y-4">
      <transition name="fade">
        <div
            v-if="notice"
            class="rounded-2xl border px-4 py-3 text-xs leading-6"
            :class="noticeType === 'error'
              ? 'border-rose-400/25 bg-rose-500/10 text-rose-100'
              : 'border-emerald-400/25 bg-emerald-500/10 text-emerald-100'"
        >
          {{ notice }}
        </div>
      </transition>

      <!-- Loading -->
      <div v-if="loading" class="rounded-3xl border border-white/10 bg-white/[0.03] py-10 text-center">
        <i class="fas fa-circle-notch fa-spin text-cyan-300 text-xl"></i>
        <p class="mt-3 text-xs text-slate-400">
          در حال دریافت تسویه‌ها...
        </p>
      </div>

      <!-- Error -->
      <div v-else-if="error" class="rounded-3xl border border-rose-400/20 bg-rose-500/10 py-10 text-center">
        <i class="fas fa-exclamation-triangle text-rose-300 text-xl"></i>
        <p class="mt-3 text-xs text-rose-200">
          {{ error }}
        </p>

        <button
            @click="fetchSettlements"
            class="mt-4 rounded-2xl border border-rose-400/30 bg-rose-500/15 px-4 py-2 text-xs font-bold text-rose-100 transition hover:bg-rose-500/25"
        >
          تلاش مجدد
        </button>
      </div>

      <template v-else>

        <!-- Summary -->
        <div class="grid grid-cols-2 gap-2">
          <div class="summary-box border-emerald-400/20 bg-emerald-400/10">
            <p>مبلغ تأییدشده</p>
            <b class="text-emerald-200" dir="ltr">{{ formatMoney(confirmedPaymentAmount) }}</b>
          </div>

          <div class="summary-box border-amber-400/20 bg-amber-400/10">
            <p>منتظر تأیید پارتنر</p>
            <b class="text-amber-200" dir="ltr">{{ formatMoney(unconfirmedPaymentAmount) }}</b>
          </div>
        </div>

        <div class="grid grid-cols-2 gap-2">
          <div class="summary-box border-cyan-400/20 bg-cyan-400/10">
            <p>نقدی تأییدشده</p>
            <b class="text-cyan-200" dir="ltr">{{ formatMoney(cashTotal) }}</b>
            <span>{{ formatNumber(cashCount) }} مورد</span>
          </div>

          <div class="summary-box border-indigo-400/20 bg-indigo-400/10">
            <p>چکی تأییدشده</p>
            <b class="text-indigo-200" dir="ltr">{{ formatMoney(checkTotal) }}</b>
            <span>{{ formatNumber(checkCount) }} مورد</span>
          </div>
        </div>

        <!-- Latest Payments -->
        <div>
          <div class="mb-3 flex items-center justify-between gap-3">
            <h4 class="text-xs font-black text-slate-200">
              آخرین پرداخت‌ها
            </h4>

            <span class="rounded-xl border border-white/10 bg-white/[0.04] px-2 py-1 text-[10px] text-slate-400">
              {{ formatNumber(total) }} پرداخت
            </span>
          </div>

          <div v-if="normalizedPayments.length" class="space-y-2">
            <article
                v-for="payment in normalizedPayments"
                :key="payment.rowKey"
                class="group rounded-3xl border border-white/10 bg-white/[0.035] p-3 transition hover:border-cyan-400/20 hover:bg-white/[0.055]"
            >
              <div class="flex items-start justify-between gap-3 min-w-0">
                <!-- Main -->
                <div class="flex min-w-0 flex-1 gap-3">
                  <a
                      v-if="payment.picture"
                      :href="imageUrl(payment.picture)"
                      target="_blank"
                      class="flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded-2xl border border-white/10 bg-slate-950"
                      title="مشاهده تصویر رسید"
                  >
                    <img
                        :src="imageUrl(payment.picture)"
                        alt="payment receipt"
                        class="h-full w-full object-cover"
                    />
                  </a>

                  <div
                      v-else
                      class="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl border border-white/10 bg-slate-950 text-slate-600"
                      title="بدون تصویر"
                  >
                    <i class="fas fa-receipt text-sm"></i>
                  </div>

                  <div class="min-w-0 flex-1">
                    <div class="flex flex-wrap items-center gap-1.5">
                      <span
                          :class="paymentBadgeClass(payment.paymentType)"
                          class="inline-flex items-center gap-1 rounded-full border px-2 py-1 text-[9px] font-bold"
                      >
                        <i :class="payment.paymentType === 'cash' ? 'fas fa-money-bill-wave' : 'fas fa-money-check-alt'"></i>
                        {{ paymentTypeLabel(payment.paymentType) }}
                      </span>

                      <span
                          :class="payment.isConfirmed
                            ? 'border-emerald-400/25 bg-emerald-400/10 text-emerald-200'
                            : 'border-amber-400/25 bg-amber-400/10 text-amber-200'"
                          class="inline-flex items-center gap-1 rounded-full border px-2 py-1 text-[9px] font-bold"
                      >
                        <i :class="payment.isConfirmed ? 'fas fa-check-circle' : 'fas fa-clock'"></i>
                        {{ payment.isConfirmed ? 'تأیید شده' : 'در انتظار تأیید پارتنر' }}
                      </span>
                    </div>

                    <p class="mt-2 truncate text-xs font-black text-white" dir="ltr">
                      {{ formatMoney(payment.amountToman) }}
                    </p>

                    <p class="mt-1 truncate text-[10px] text-slate-500">
                      {{ payment.note || payment.title }}
                    </p>

                    <p
                        v-if="payment.trackingCode"
                        class="mt-1 truncate text-[10px] text-slate-400"
                        dir="ltr"
                    >
                      کد پیگیری: {{ payment.trackingCode }}
                    </p>
                  </div>
                </div>

                <!-- Side -->
                <div class="shrink-0 text-left">
                  <p class="text-[10px] text-slate-400">
                    {{ formatDateOnly(payment.date) }}
                  </p>

                  <p
                      v-if="payment.paymentType === 'check'"
                      class="mt-1 text-[10px]"
                      :class="payment.isPendingCheck ? 'text-orange-300' : 'text-emerald-300'"
                  >
                    {{ payment.isPendingCheck ? 'سررسیدنشده' : 'سررسیدشده' }}
                  </p>
                </div>
              </div>

              <!-- Partner confirm action -->
              <div
                  v-if="canPartnerConfirm(payment)"
                  class="mt-3 rounded-2xl border border-amber-400/20 bg-amber-400/[0.06] p-3"
              >
                <div class="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <p class="text-[11px] font-bold text-amber-100">
                      این پرداخت منتظر تأیید شماست.
                    </p>
                    <p class="mt-1 text-[10px] leading-5 text-amber-100/60">
                      بعد از تأیید، مبلغ در پرداخت‌های قطعی پارتنر حساب می‌شود.
                    </p>
                  </div>

                  <button
                      type="button"
                      @click="openConfirmModal(payment)"
                      :disabled="confirmLoading"
                      class="rounded-2xl bg-gradient-to-l from-emerald-300 to-cyan-300 px-4 py-2 text-[11px] font-black text-slate-950 shadow-lg transition hover:brightness-110 disabled:opacity-50"
                  >
                    تأیید نهایی پرداخت
                  </button>

                </div>
              </div>
            </article>
          </div>

          <div
              v-else
              class="rounded-3xl border border-white/10 bg-white/[0.03] py-8 text-center"
          >
            <p class="text-xs text-slate-500">
              هیچ پرداختی یافت نشد.
            </p>
          </div>
        </div>
      </template>
    </div>

    <transition name="fade">
      <div
          v-if="confirmModal.visible"
          class="fixed inset-0 z-[999999] flex items-center justify-center bg-black/80 p-4 backdrop-blur-md"
          @click.self="closeConfirmModal"
      >
        <div class="w-full max-w-md overflow-hidden rounded-3xl border border-white/10 bg-slate-950 shadow-2xl">
          <div class="border-b border-white/10 bg-emerald-400/[0.05] p-5">
            <p class="text-[11px] text-slate-400">
              تأیید نهایی توسط پارتنر
            </p>
            <h3 class="mt-1 text-lg font-black text-white">
              تأیید پرداخت {{ paymentTypeLabel(confirmModal.payment.paymentType) }}
            </h3>
          </div>

          <div class="space-y-4 p-5">
            <div class="rounded-2xl border border-white/10 bg-white/[0.035] p-4 text-sm leading-7 text-slate-300">
              مبلغ:
              <b class="text-emerald-200" dir="ltr">
                {{ formatMoney(confirmModal.payment.amountToman) }}
              </b>
              <br>
              بعد از تأیید، این پرداخت در مانده قطعی حساب پارتنر لحاظ می‌شود.
            </div>

            <div class="grid grid-cols-2 gap-3">
              <button
                  type="button"
                  @click="closeConfirmModal"
                  :disabled="confirmLoading"
                  class="rounded-2xl border border-white/10 bg-white/[0.04] px-4 py-3 text-xs font-bold text-slate-300 disabled:opacity-50"
              >
                انصراف
              </button>

              <button
                  type="button"
                  @click="runPartnerConfirm"
                  :disabled="confirmLoading"
                  class="rounded-2xl bg-gradient-to-l from-emerald-300 to-cyan-300 px-4 py-3 text-xs font-black text-slate-950 disabled:opacity-50"
              >
                {{ confirmLoading ? 'در حال تأیید...' : 'تأیید نهایی' }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </transition>
  </aside>
</template>

<script>
export default {
  name: 'SettlementSection',

  props: {
    title: {
      type: String,
      default: 'تسویه حساب‌ها'
    },

    subtitle: {
      type: String,
      default: 'خلاصه پرداخت‌های نقدی و چکی'
    },

    partnerId: {
      type: [String, Number],
      default: null
    },


    limit: {
      type: Number,
      default: 50
    },

    financialBase: {
      type: String,
      default: 'finance/'
    }
  },

  data() {
    return {
      loading: false,
      error: null,
      notice: '',
      noticeType: 'success',

      confirmLoading: false,

      confirmModal: {
        visible: false,
        payment: {}
      },

      payments: [],
      total: 0
    }
  },

  computed: {
    isPartnerUser() {
      return this.user && this.user.user_type === 'prtnr'
    },

    normalizedPayments() {
      return (this.payments || []).map((payment) => {
        const paymentType = payment.payment_type || payment.type || 'unknown'
        const date = payment.date || payment.paid_at || payment.issue_date || null
        const dueDate = payment.due_date || null

        return {
          rowKey: `${paymentType}-${payment.id}`,
          id: payment.id,

          paymentType,
          partnerId: payment.partner_id,

          amount: payment.amount || 0,
          amountToman: payment.amount_toman || payment.amount || 0,

          currencyCode: payment.currency_code || 'IRT',
          currencyTitle: payment.currency_title || 'تومان',
          exchangeRateToToman: payment.exchange_rate_to_toman || 1,

          date,
          dueDate,

          note: payment.note || '',
          title: this.buildPaymentTitle(paymentType, payment),
          isPendingCheck: this.isFutureDate(dueDate),

          picture: payment.picture || null,
          trackingCode: payment.tracking_code || '',
          isConfirmed: Boolean(payment.is_confirmed)
        }
      })
    },

    confirmedPaymentAmount() {
      return this.normalizedPayments
          .filter(item => item.isConfirmed)
          .reduce((sum, item) => sum + Number(item.amountToman || 0), 0)
    },

    unconfirmedPaymentAmount() {
      return this.normalizedPayments
          .filter(item => !item.isConfirmed)
          .reduce((sum, item) => sum + Number(item.amountToman || 0), 0)
    },

    cashTotal() {
      return this.normalizedPayments
          .filter(item => item.paymentType === 'cash' && item.isConfirmed)
          .reduce((sum, item) => sum + Number(item.amountToman || 0), 0)
    },

    checkTotal() {
      return this.normalizedPayments
          .filter(item => item.paymentType === 'check' && item.isConfirmed)
          .reduce((sum, item) => sum + Number(item.amountToman || 0), 0)
    },

    cashCount() {
      return this.normalizedPayments.filter(item => item.paymentType === 'cash').length
    },

    checkCount() {
      return this.normalizedPayments.filter(item => item.paymentType === 'check').length
    }
  },

  watch: {
    partnerId() {
      this.fetchSettlements()
    }
  },

  mounted() {
    this.fetchSettlements()
  },

  methods: {
    toast(message, type = 'success') {
      this.notice = message
      this.noticeType = type

      setTimeout(() => {
        this.notice = ''
      }, 3500)
    },

    buildPaymentTitle(paymentType, payment) {
      if (paymentType === 'cash') return 'پرداخت نقدی'

      if (paymentType === 'check') {
        return payment.due_date
            ? `چک / سررسید ${this.formatDateOnly(payment.due_date)}`
            : 'پرداخت چکی'
      }

      return 'پرداخت'
    },

    paymentTypeLabel(type) {
      if (type === 'cash') return 'نقدی'
      if (type === 'check') return 'چک'
      return 'نامشخص'
    },

    paymentBadgeClass(type) {
      if (type === 'cash') {
        return 'border-cyan-400/25 bg-cyan-400/10 text-cyan-200'
      }

      if (type === 'check') {
        return 'border-indigo-400/25 bg-indigo-400/10 text-indigo-200'
      }

      return 'border-slate-400/20 bg-slate-400/10 text-slate-300'
    },

    canPartnerConfirm(payment) {
      if (!payment || payment.isConfirmed) return false
      if (!this.isPartnerUser) return false

      if (this.partnerId && payment.partnerId) {
        return String(this.partnerId) === String(payment.partnerId)
      }

      return true
    },

    openConfirmModal(payment) {
      if (!this.canPartnerConfirm(payment)) {
        this.toast('شما مجاز به تأیید این پرداخت نیستید.', 'error')
        return
      }

      this.confirmModal = {
        visible: true,
        payment
      }
    },

    closeConfirmModal(force = false) {
      if (this.confirmLoading && !force) return

      this.confirmModal = {
        visible: false,
        payment: {}
      }
    },

    buildFinancialEndpoint(path) {
      if (this.endpoint && typeof this.endpoint === 'function') {
        return this.endpoint(`${this.financialBase}${path}`)
      }

      return `${this.financialBase}${path}`
    },

    runPartnerConfirm() {
      const payment = this.confirmModal.payment

      if (!payment || !payment.id || !payment.paymentType) return

      if (!this.canPartnerConfirm(payment)) {
        this.toast('شما مجاز به تأیید این پرداخت نیستید.', 'error')
        return
      }

      this.confirmLoading = true

      const resource = payment.paymentType === 'check' ? 'check' : 'cash'

      /*
       * این مسیر با API فعلی CRUD سازگار است:
       * PATCH finance/suppliers/payments/cash/<id>/
       * PATCH finance/suppliers/payments/check/<id>/
       *
       * اگر backend endpoint اختصاصی confirm ساختی،
       * همین url را به confirm-by-partner تغییر بده.
       */
      const url = this.buildFinancialEndpoint(`suppliers/payments/${resource}/${payment.id}/`)

      this.request({
        url,
        method: 'patch',
        data: {
          is_confirmed: true
        },
        success: () => {
          this.confirmLoading = false
          this.closeConfirmModal(true)
          this.toast('پرداخت با موفقیت توسط پارتنر تأیید شد.')
          this.fetchSettlements()
          this.$emit('payment-confirmed')
        },
        error: (err) => {
          console.error('partner confirm payment error:', err)
          this.toast(this.getApiErrorMessage(err, 'خطا در تأیید پرداخت'), 'error')
          this.confirmLoading = false
        }
      })
    },

    isFutureDate(dateString) {
      if (!dateString) return false

      const date = new Date(dateString)
      if (Number.isNaN(date.getTime())) return false

      const today = new Date()
      today.setHours(0, 0, 0, 0)

      date.setHours(0, 0, 0, 0)

      return date > today
    },

    formatNumber(value) {
      if (value === null || value === undefined || value === '') return '۰'

      const number = Number(value)
      if (Number.isNaN(number)) return '۰'

      return new Intl.NumberFormat('fa-IR').format(number)
    },

    formatMoney(value) {
      return `${this.formatNumber(Math.round(Number(value || 0)))} تومان`
    },

    formatDateOnly(dateString) {
      if (!dateString) return '-'

      const date = new Date(dateString)
      if (Number.isNaN(date.getTime())) return '-'

      return new Intl.DateTimeFormat('fa-IR', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      }).format(date)
    },

    imageUrl(url) {
      if (!url) return ''
      if (String(url).startsWith('http')) return url

      if (typeof this.img_source === 'function') {
        return this.img_source(url)
      }

      if (typeof this.image_source === 'function') {
        return this.image_source(url)
      }

      return `https://api.exuni.ir${url}`
    },

    buildEndpoint(path) {
      if (this.endpoint && typeof this.endpoint === 'function') {
        return this.endpoint(path)
      }

      return path
    },

    fetchSettlements() {
      this.loading = true
      this.error = null

      let url = this.buildEndpoint('deliver/chart/payments')

      const params = []

      params.push('payment_type=both')
      params.push('is_confirmed=all')
      params.push('page=1')
      params.push(`page_size=${encodeURIComponent(this.limit)}`)

      if (this.partnerId) {
        params.push(`partner_id=${encodeURIComponent(this.partnerId)}`)
      }

      url += `?${params.join('&')}`

      this.request({
        url,
        method: 'get',
        success: (response) => {
          this.payments = Array.isArray(response.results) ? response.results : []
          this.total = Number(response.count || 0)
          this.loading = false
        },
        error: (err) => {
          console.error('SettlementSection API error:', err)
          this.error = 'خطا در دریافت تسویه‌ها'
          this.loading = false
        }
      })
    },

    getApiErrorMessage(error, fallback) {
      if (!error) return fallback

      if (typeof error === 'string') return error

      const data = error.response && error.response.data
          ? error.response.data
          : error.data

      if (data) {
        if (typeof data === 'string') return data
        if (data.detail) return data.detail
        if (data.error) return data.error

        const firstKey = Object.keys(data)[0]

        if (firstKey) {
          if (Array.isArray(data[firstKey])) return data[firstKey][0]
          return String(data[firstKey])
        }
      }

      return fallback
    }
  }
}
</script>

<style scoped>
.settlement-section,
.settlement-section * {
  box-sizing: border-box;
}

.settlement-section {
  contain: layout paint;
}

.summary-box {
  border-width: 1px;
  border-radius: 1.25rem;
  padding: 0.85rem;
  min-width: 0;
}

.summary-box p {
  font-size: 10px;
  color: rgb(203 213 225);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.summary-box b {
  display: block;
  margin-top: 0.35rem;
  font-size: 12px;
  font-weight: 900;
}

.summary-box span {
  display: block;
  margin-top: 0.25rem;
  font-size: 10px;
  color: rgb(100 116 139);
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}

.truncate {
  min-width: 0;
}

.min-w-0 {
  min-width: 0 !important;
}

button {
  max-width: 100%;
}

img {
  max-width: 100%;
}
</style>